import React, { useState, useEffect } from 'react';
import { fetchStats } from '../../services/api';
import { Link } from 'react-router-dom';
import './StatsView.css';

const StatsView = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    const loadStats = async () => {
      try {
        setLoading(true);
        const data = await fetchStats();
        setStats(data);
        setError(null);
      } catch (err) {
        console.error('Error loading stats:', err);
        setError('Failed to load statistics. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    loadStats();
  }, []);

  if (loading) {
    return <div className="loading">Loading statistics...</div>;
  }

  if (error) {
    return <div className="error">{error}</div>;
  }

  if (!stats) {
    return <div className="no-data">No statistics data available</div>;
  }

  const { circuits, overall, analysis, relevance, recent_relevant_decisions } = stats;

  return (
    <div className="stats-view">
      <header>
        <h1>Defense Nuggets Statistics</h1>
        <Link to="/" className="home-link">Back to Home</Link>
      </header>

      <section className="stats-section overall-stats">
        <h2>Overall Statistics</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Decisions</h3>
            <div className="stat-value">{overall.total_count}</div>
            <div className="stat-description">
              From {overall.date_range}
            </div>
          </div>
          
          <div className="stat-card">
            <h3>Criminal Cases</h3>
            <div className="stat-value">{overall.criminal_count}</div>
            <div className="stat-percent">{overall.criminal_percent}%</div>
          </div>
          
          <div className="stat-card">
            <h3>Civil Cases</h3>
            <div className="stat-value">{overall.civil_count}</div>
            <div className="stat-percent">{overall.civil_percent}%</div>
          </div>
          
          <div className="stat-card">
            <h3>Published Opinions</h3>
            <div className="stat-value">{overall.published}</div>
            <div className="stat-percent">{overall.published_percent}%</div>
          </div>
          
          <div className="stat-card">
            <h3>Unpublished Opinions</h3>
            <div className="stat-value">{overall.unpublished}</div>
            <div className="stat-percent">{overall.unpublished_percent}%</div>
          </div>
          
          <div className="stat-card">
            <h3>Unknown Type</h3>
            <div className="stat-value">{overall.unknown_type}</div>
            <div className="stat-percent">{overall.unknown_type_percent}%</div>
          </div>
        </div>
      </section>

      <section className="stats-section analysis-stats">
        <h2>Analysis Statistics</h2>
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Downloaded</h3>
            <div className="stat-value">{analysis.downloaded}</div>
            <div className="stat-percent">{analysis.downloaded_percent}%</div>
          </div>
          
          <div className="stat-card">
            <h3>Analyzed</h3>
            <div className="stat-value">{analysis.analyzed}</div>
            <div className="stat-percent">{analysis.analyzed_percent}%</div>
          </div>
          
          <div className="stat-card">
            <h3>With Summaries</h3>
            <div className="stat-value">{analysis.with_summary}</div>
            <div className="stat-percent">{analysis.with_summary_percent}%</div>
          </div>
          
          <div className="stat-card">
            <h3>With Scores</h3>
            <div className="stat-value">{analysis.with_scores}</div>
            <div className="stat-percent">{analysis.with_scores_percent}%</div>
          </div>
          
          <div className="stat-card">
            <h3>With Analysis</h3>
            <div className="stat-value">{analysis.with_analysis}</div>
            <div className="stat-percent">{analysis.with_analysis_percent}%</div>
          </div>
        </div>
      </section>

      <section className="stats-section relevance-stats">
        <h2>Relevance Statistics</h2>
        <div className="stats-row">
          <div className="stat-card relevance-overview">
            <h3>Relevant Decisions (Score > 0)</h3>
            <div className="stat-value">{relevance.relevant_count}</div>
            <div className="stat-percent">{relevance.relevant_percent}% of scored decisions</div>
          </div>
          
          <div className="relevance-chart">
            <h3>Score Distribution</h3>
            <div className="score-bars">
              {relevance.score_distribution.map(item => (
                <div 
                  key={`score-${item.score}`} 
                  className="score-bar-container"
                >
                  <div className="score-label">Score {item.score}</div>
                  <div className="score-bar-wrapper">
                    <div 
                      className={`score-bar score-${item.score}`} 
                      style={{ width: `${Math.max(item.percentage, 2)}%` }}
                    >
                      <span className="score-count">{item.count}</span>
                    </div>
                  </div>
                  <div className="score-percent">{item.percentage}%</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="stats-section circuit-stats">
        <h2>Circuit Court Statistics</h2>
        <div className="table-responsive">
          <table>
            <thead>
              <tr>
                <th>Circuit</th>
                <th>Total</th>
                <th>Criminal</th>
                <th>Civil</th>
                <th>Published</th>
                <th>Unpublished</th>
                <th>Unknown</th>
                <th>Analyzed</th>
                <th>Relevant</th>
                <th>Avg. Score</th>
                <th>Date Range</th>
              </tr>
            </thead>
            <tbody>
              {circuits.map(circuit => (
                <tr key={`circuit-${circuit.number}`}>
                  <td>{circuit.number}</td>
                  <td>{circuit.total}</td>
                  <td>{circuit.criminal}</td>
                  <td>{circuit.civil}</td>
                  <td>{circuit.published}</td>
                  <td>{circuit.unpublished}</td>
                  <td>{circuit.unknown_type}</td>
                  <td>{circuit.analyzed}</td>
                  <td>{circuit.relevant_count}</td>
                  <td>{circuit.avg_relevance}</td>
                  <td>{circuit.date_range}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {recent_relevant_decisions.length > 0 && (
        <section className="stats-section recent-relevant">
          <h2>Relevant Decisions (Last 2 Weeks)</h2>
          <div className="relevant-decisions-list">
            {recent_relevant_decisions.map(decision => (
              <div key={`decision-${decision.id}`} className="relevant-decision-card">
                <div className="decision-header">
                  <h3>
                    {decision.case_name}
                    <span className="decision-type" style={{ fontStyle: 'italic' }}>
                      {'  (' + (decision.decision_type === 'published' ? 'Published' : 'Unpublished') + ')'}
                    </span>
                  </h3>
                  <div className="decision-meta">
                    <span className="circuit">Circuit {decision.circuit}</span>
                    <span className="date">{new Date(decision.filing_date).toLocaleDateString()}</span>
                    <span className={`relevance relevance-${decision.relevance_score}`}>
                      Relevance: {decision.relevance_score}/4
                    </span>
                  </div>
                </div>
                {decision.defense_analysis && (
                  <div className="defense-analysis">
                    <h4>Defense Analysis:</h4>
                    <p dangerouslySetInnerHTML={{ __html: decision.defense_analysis }}></p>
                  </div>
                )}
                <div className="decision-actions">
                  <a 
                    href={decision.pdf_url} 
                    target="_blank" 
                    rel="noopener noreferrer" 
                    className="pdf-link"
                  >
                    View PDF
                  </a>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}
    </div>
  );
};

export default StatsView;